class FacetFiltersForm extends HTMLElement {
    #gridContainerId;
    #gridId;

    constructor() {
        super();

        this.#gridContainerId = this.getAttribute('gridcontainer') ?? 'ProductGridContainer';
        this.#gridId = this.getAttribute('grid') ?? 'product-grid';

        this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

        const onSubmit = this.onSubmitHandler.bind(this);
        this.debouncedOnSubmit = debounce(event => onSubmit(event), 500);

        this.querySelector('form').addEventListener('input', this.debouncedOnSubmit.bind(this));

        const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
        if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);

        document.querySelectorAll('.js-filter').forEach(filter => this.filterA11y(filter));
    }

    setListeners() {
        const onHistoryChange = (event) => {
            const searchParams = event.state ? event.state.searchParams : this.searchParamsInitial;
            if (searchParams === this.searchParamsPrev) return;
            this.renderPage(searchParams, null, false);
        }
        window.addEventListener('popstate', onHistoryChange);   
    }

    toggleActiveFacets(disable = true) {
        document.querySelectorAll('.js-facet-remove').forEach((element) => {
            element.classList.toggle('disabled', disable);
        });
    }

    renderPage(searchParams, event, updateURLHash = true) {
        this.searchParamsPrev = searchParams;
        const sections = this.getSections();
        const countContainer = document.getElementById('ProductCount');
        const countContainerDesktop = document.getElementById('ProductCountDesktop');
        document.getElementById(this.#gridContainerId).querySelector('.collection').classList.add('loading');
        if (countContainer) {
            countContainer.classList.add('loading');
        }
        if (countContainerDesktop) {
            countContainerDesktop.classList.add('loading');
        }

        sections.forEach((section) => {
            const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
            const filterDataUrl = element => element.url === url;

            this.filterData.some(filterDataUrl) ?
                this.renderSectionFromCache(filterDataUrl, event) :
                this.renderSectionFromFetch(url, event);
        });

        if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
    }

    renderSectionFromFetch(url, event) {
        fetch(url)
            .then(response => response.text())
            .then((responseText) => {
                const html = responseText;
                this.filterData = [...this.filterData, {html, url}];
                this.renderFilters(html, event);
                this.renderProductGridContainer(html);
                FacetFiltersForm.renderProductCount(html);
            });
    }

    renderSectionFromCache(filterDataUrl, event) {
        const html = this.filterData.find(filterDataUrl).html;
        this.renderFilters(html, event);
        this.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
    }

    renderProductGridContainer(html) {
        document.getElementById(this.#gridContainerId).innerHTML = new DOMParser().parseFromString(html, 'text/html').getElementById(this.#gridContainerId).innerHTML;
    }

    static renderProductCount(html) {
        const productCountElement = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount');
        if (!productCountElement) {
            return;
        }
        const count = productCountElement.innerHTML
        const container = document.getElementById('ProductCount');
        const containerDesktop = document.getElementById('ProductCountDesktop');
        container.innerHTML = count;
        container.classList.remove('loading');
        if (containerDesktop) {
            containerDesktop.innerHTML = count;
            containerDesktop.classList.remove('loading');
        }
    }

    renderFilters(html, event) {
        const parsedHTML = new DOMParser().parseFromString(html, 'text/html');

        const facetDetailsElements =
            parsedHTML.querySelectorAll('#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter');
        const matchesIndex = (element) => {
            const jsFilter = event ? event.target.closest('.js-filter') : undefined;
            return jsFilter ? element.dataset.index === jsFilter.dataset.index : false;
        }
        const facetsToRender = Array.from(facetDetailsElements).filter(element => !matchesIndex(element));
        const countsToRender = Array.from(facetDetailsElements).find(matchesIndex);

        facetsToRender.forEach((element) => {
            const filter = document.querySelector(`.js-filter[data-index="${element.dataset.index}"]`);
            filter.innerHTML = element.innerHTML;
            this.filterA11y(filter);
        });

        this.renderActiveFacets(parsedHTML);
        FacetFiltersForm.renderAdditionalElements(parsedHTML);

        if (countsToRender) FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
    }

    renderActiveFacets(html) {
        const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

        activeFacetElementSelectors.forEach((selector) => {
            const activeFacetsElement = html.querySelector(selector);
            if (!activeFacetsElement) return;
            document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
        })

        this.toggleActiveFacets(false);
    }

    static renderAdditionalElements(html) {
        const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

        mobileElementSelectors.forEach((selector) => {
            if (!html.querySelector(selector)) return;
            document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
        });

        document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
    }

    static renderCounts(source, target) {
        const targetElement = target.querySelector('.facets__selected');
        const sourceElement = source.querySelector('.facets__selected');

        if (sourceElement && targetElement) {
            target.querySelector('.facets__selected').outerHTML = source.querySelector('.facets__selected').outerHTML;
            target.querySelector('.facets__summary').outerHTML = source.querySelector('.facets__summary').outerHTML;
        }
    }

    static updateURLHash(searchParams) {
        history.pushState({searchParams}, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
    }

    getSections() {
        return [
            {
                section: document.getElementById(this.#gridId).dataset.id,
            }
        ]
    }

    onSubmitHandler(event) {
        event.preventDefault();
        const formData = new FormData(event.target.closest('form'));
        const searchParams = new URLSearchParams(formData).toString();
        this.renderPage(searchParams, event);
    }

    onActiveFilterClick(event) {
        event.preventDefault();
        this.toggleActiveFacets();
        const url = event.currentTarget.href.indexOf('?') == -1 ? '' : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
        this.renderPage(url);
    }

    filterA11y(filterElement) {
        filterElement.querySelectorAll('input[type=checkbox]').forEach(el =>
        el.setAttribute('tabindex', '-1'));
        filterElement.querySelectorAll('[role=checkbox]').forEach(el => {
            el.setAttribute('tabindex', '0');
            el.addEventListener('keydown', event => {
                let checkbox = el.parentNode.querySelector('input[type=checkbox]');
                if (event.code === 'Space') {
                    event.stopImmediatePropagation();
                    event.preventDefault();
                    checkbox.click();
                }   
            }, false);
        });
    }
}

customElements.define('facet-filters-form', FacetFiltersForm);

document.querySelectorAll('facet-filters-form').forEach((f => {
    f.filterData = [];
    f.searchParamsInitial = window.location.search.slice(1);
    f.searchParamsPrev = window.location.search.slice(1);
    f.setListeners()
}));

class PriceRange extends HTMLElement {
    constructor() {
        super();
        this.querySelectorAll('input')
            .forEach(element => element.addEventListener('change', this.onRangeChange.bind(this)));

        this.setMinAndMaxValues();
    }

    onRangeChange(event) {
        this.adjustToValidValues(event.currentTarget);
        this.setMinAndMaxValues();
    }

    setMinAndMaxValues() {
        const inputs = this.querySelectorAll('input');
        const minInput = inputs[0];
        const maxInput = inputs[1];
        if (maxInput.value) minInput.setAttribute('max', maxInput.value);
        if (minInput.value) maxInput.setAttribute('min', minInput.value);
        if (minInput.value === '') maxInput.setAttribute('min', 0);
        if (maxInput.value === '') minInput.setAttribute('max', maxInput.getAttribute('max'));
    }

    adjustToValidValues(input) {
        const value = Number(input.value);
        const min = Number(input.getAttribute('min'));
        const max = Number(input.getAttribute('max'));

        if (value < min) input.value = min;
        if (value > max) input.value = max;
    }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
    constructor() {
        super();
        this.querySelector('a').addEventListener('click', (event) => {
            event.preventDefault();
            const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
            form.onActiveFilterClick(event);
        });
    }
}

customElements.define('facet-remove', FacetRemove);